import React, { useMemo } from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Outlet, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { LoadingIndicator } from 'ts/common/components';
import { getTheme } from 'ts/common/components/gallery';
import ClientInvoicePageLayout from 'ts/common/components/invoice/ClientInvoicePageLayout';
import { useTranslate } from 'ts/common/hooks';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import { useClientApiFetch } from '../bootstrap';
import { FetchMethod } from '../bootstrap';

interface InvoiceError {
    detail?: string;
    status: number;
}

const isInvoiceError = (response: unknown): response is InvoiceError => {
    return (
        response !== null &&
        typeof response === 'object' &&
        'status' in response &&
        typeof (response as InvoiceError).status === 'number' &&
        (response as InvoiceError).status >= 400
    );
};

const InvoiceAppContent: React.FC = () => {
    const t = useTranslate('clientInvoice');
    const { invoiceId } = useParams<{ invoiceId: string }>();

    const {
        response: invoice,
        loading,
        performFetch: refetchInvoice
    } = useClientApiFetch<SpApi.Client.IInvoice | InvoiceError>(`invoice/${invoiceId}`, {
        method: FetchMethod.GET
    });

    const theme = useMemo(
        () =>
            invoice && !isInvoiceError(invoice)
                ? extendTheme(getTheme(invoice.brandTheme))
                : undefined,
        [invoice]
    );

    if (!theme) {
        return null;
    }

    // TODO: Improve loading and error UI

    if (loading) {
        return (
            <ChakraProvider resetCSS={false} theme={theme}>
                <LoadingIndicator />
            </ChakraProvider>
        );
    }

    if (!invoice) {
        return (
            <div>
                <h1>{t('invoiceNotFound')}</h1>
                <p>{t('invoiceNotFoundMessage')}</p>
            </div>
        );
    }

    if (isInvoiceError(invoice)) {
        return (
            <div>
                <h1>{t('errorLoadingInvoice')}</h1>
                <p>{t('unableToLoadInvoice')}</p>
            </div>
        );
    }

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            <ClientInvoicePageLayout invoice={invoice}>
                <Outlet context={{ invoice, refetchInvoice }} />
            </ClientInvoicePageLayout>
        </ChakraProvider>
    );
};

const InvoiceApp: React.FC = withBootstrapData(() => {
    const { translations } = useBootstrapData();

    const PageWithTranslations = useMemo(
        () => withI18NextTranslate(InvoiceAppContent, translations),
        [translations]
    );

    return <PageWithTranslations />;
});

export default InvoiceApp;
